import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, ClockIcon, TableIcon, UsersIcon } from '@/components/icons';
import { formatTime, formatSlotDuration } from '@/lib/utils';
import { BookingCategoryImageCard } from '@/components/ui/BookingCategoryImageCard';
import type { CartItemResponse } from '@/features/cart/types';

interface CartItemHeaderProps {
  event: { name: string; description?: string | null } | null;
  eventSchedule: { start_time?: string | null; duration?: number } | null;
  bookingCategory: { name?: string; is_table_booking?: boolean; card_image_id?: string | null } | null;
  eventBookingCategorySlotDuration?: number | null;
  cartItem: CartItemResponse;
  businessTimeZone: string;
  eventDate: string;
  eventTime: string;
  canEdit: boolean;
  isEditing: boolean;
  isGuestCart?: boolean;
  editGuestCount?: number;
  onStartEdit: () => void;
  onDeleteClick: () => void;
  onOpenGuestModal?: () => void;
}

const CartItemHeader: React.FC<CartItemHeaderProps> = ({
  event,
  bookingCategory,
  eventBookingCategorySlotDuration,
  cartItem,
  businessTimeZone,
  eventDate,
  eventTime,
  canEdit,
  isEditing,
  isGuestCart,
  editGuestCount,
  onStartEdit,
  onDeleteClick,
  onOpenGuestModal,
}) => {
  const navigate = useNavigate();

  // Check if booking category has a valid image
  const hasBookingCategoryImage = bookingCategory?.card_image_id &&
    bookingCategory.card_image_id !== '00000000-0000-0000-0000-000000000000';

  // Show image for both ticket and table bookings when available
  const showBookingCategoryImage = hasBookingCategoryImage &&
    cartItem.event_booking_category?.id;

  const handleImageClick = () => {
    if (cartItem.event_booking_category?.id) {
      const cartType = isGuestCart ? 'guest' : 'main';
      const bookingType = bookingCategory?.is_table_booking ? 'tables' : 'tickets';
      navigate(`/booking/${bookingType}/${cartItem.event_booking_category.id}?cartType=${cartType}&cartItemId=${cartItem.id}`);
    }
  };

  return (
    <div
      className="p-6 border-b border-gray-700/30"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-6 flex-1 min-w-0">
          {/* Booking Category Image - Top Left */}
          {showBookingCategoryImage && (
            <BookingCategoryImageCard
              imageId={bookingCategory?.card_image_id}
              categoryName={bookingCategory?.name}
              onClick={handleImageClick}
              showLinkBadge={true}
            />
          )}

          {/* Cart Details - Right of Image */}
          <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">{event?.name}</h3>

              {/* Event Description */}
              {event?.description && (
                <p className="text-sm text-gray-400 mb-4 line-clamp-2 leading-relaxed">
                  {event.description}
                </p>
              )}
            </div>

            {/* Date, Time, and Guests */}
            <div className="space-y-2">
              {/* Row 1: Date and Event Time */}
              <div className="flex items-center gap-4 text-base">
                <div className="flex items-center gap-2 min-w-[170px]">
                  <div className="w-4 h-4">
                    <CalendarIcon />
                  </div>
                  <span className="font-bold text-gray-300">{eventDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4">
                    <ClockIcon />
                  </div>
                  <span className="font-bold text-gray-300">
                    Entry Time: <span className="text-gray-300">{eventTime}</span>
                  </span>
                </div>
              </div>

              {/* Row 2: Guests and Event Duration */}
              <div className="flex items-center gap-4 text-base">
                <div className="flex items-center gap-2 min-w-[170px]">
                  <div className="w-4 h-4">
                    <UsersIcon />
                  </div>
                  <span className="font-bold text-gray-300">
                    {isEditing ? `${editGuestCount || cartItem.total_guests} Guest${(editGuestCount || cartItem.total_guests) !== 1 ? 's' : ''}` : `${cartItem.total_guests} Guest${cartItem.total_guests !== 1 ? 's' : ''}`}
                  </span>
                  {isEditing && (editGuestCount || cartItem.total_guests) > 1 && onOpenGuestModal && (
                    <button
                      onClick={onOpenGuestModal}
                      className="ml-2 px-3 py-1.5 text-sm text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-gray-600 hover:border-red-500/30"
                      title="Remove guests"
                    >
                      Remove Guests
                    </button>
                  )}
                </div>
              </div>
            </div>

            {bookingCategory && !showBookingCategoryImage && (
              <p className="text-base text-gray-400 mt-2">{bookingCategory.name}</p>
            )}
          </div>
        </div>

        {/* Edit and Delete Buttons - Only show if cart status is ACTIVE and not editing */}
        {!isEditing && canEdit && (
          <div className="flex items-center gap-3">
            <button
              onClick={onStartEdit}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
              title="Edit item"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={onDeleteClick}
              className="p-2 text-gray-400 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10"
              title="Remove item"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Table Booking Specific Info */}
      {bookingCategory?.is_table_booking && (
        <div className="mt-3 pt-3 border-t border-gray-700/30">
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Reservation Details</h4>
          <div className="flex flex-wrap items-center gap-4 text-base">
            {cartItem.table_number && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4">
                  <TableIcon className="w-4 h-4" />
                </div>
                <span className="font-bold text-gray-300">
                  Table: <span className="text-gray-300">{cartItem.table_number}</span>
                </span>
              </div>
            )}
            {cartItem.table_size && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4">
                  <UsersIcon />
                </div>
                <span className="font-bold text-gray-300">
                  Capacity: <span className="text-gray-300">{cartItem.table_size}</span>
                </span>
              </div>
            )}
            {cartItem.booking_time && (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4">
                  <ClockIcon />
                </div>
                <span className="font-bold text-gray-300">
                  Reservation Time: <span className="text-gray-300">
                    {formatTime(cartItem.booking_time, businessTimeZone)}
                    {eventBookingCategorySlotDuration && eventBookingCategorySlotDuration > 0 && (
                      <> (Duration: {formatSlotDuration(eventBookingCategorySlotDuration)})</>
                    )}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CartItemHeader;
